import {
  handAngles,
  numeralPositions,
  polar,
} from "@/lib/tool-kit/worksheets/clock";

export type NumeralMode = "all" | "quarters" | "none";

const CENTER = 120;
const FACE = 100;
const INK = "#171717";

/**
 * An analog clock face as SVG. With `hands` it shows a time; without, a blank face for
 * drawing on. Everything is vector, so it prints crisply at any size.
 */
export function ClockFace({
  hour,
  minute,
  hands,
  numerals,
  ticks,
  minuteNumbers,
  label,
}: {
  hour: number;
  minute: number;
  hands: boolean;
  numerals: NumeralMode;
  ticks: boolean;
  minuteNumbers: boolean;
  label: string;
}) {
  const angles = handAngles(hour, minute);
  const hourTip = polar(CENTER, CENTER, angles.hour, 52);
  const minuteTip = polar(CENTER, CENTER, angles.minute, 80);
  const crop = minuteNumbers ? 0 : 12;
  return (
    <svg
      aria-label={label}
      className="mx-auto block h-auto w-full max-w-[2.4in]"
      role="img"
      viewBox={`${crop} ${crop} ${240 - 2 * crop} ${240 - 2 * crop}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx={CENTER}
        cy={CENTER}
        fill="#ffffff"
        r={FACE}
        stroke={INK}
        strokeWidth={3}
      />
      {ticks
        ? Array.from({ length: 60 }, (_, i) => {
            const major = i % 5 === 0;
            const outer = polar(CENTER, CENTER, i * 6, FACE - 2);
            const inner = polar(CENTER, CENTER, i * 6, FACE - (major ? 11 : 6));
            return (
              <line
                key={`tick-${i + 1}`}
                stroke={INK}
                strokeWidth={major ? 2 : 1}
                x1={outer.x}
                x2={inner.x}
                y1={outer.y}
                y2={inner.y}
              />
            );
          })
        : null}
      {numerals === "none"
        ? null
        : numeralPositions(CENTER, CENTER, 76)
            .filter(
              (p) => numerals === "all" || [12, 3, 6, 9].includes(p.value)
            )
            .map((p) => (
              <text
                dominantBaseline="central"
                fill={INK}
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                fontSize={22}
                fontWeight={600}
                key={p.value}
                textAnchor="middle"
                x={p.x}
                y={p.y}
              >
                {p.value}
              </text>
            ))}
      {minuteNumbers
        ? Array.from({ length: 12 }, (_, i) => {
            const spot = polar(CENTER, CENTER, (i + 1) * 30, FACE + 13);
            return (
              <text
                dominantBaseline="central"
                fill="#525252"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                fontSize={11}
                key={`minute-${i + 1}`}
                textAnchor="middle"
                x={spot.x}
                y={spot.y}
              >
                {(i + 1) * 5}
              </text>
            );
          })
        : null}
      {hands ? (
        <>
          <line
            stroke={INK}
            strokeLinecap="round"
            strokeWidth={7}
            x1={CENTER}
            x2={hourTip.x}
            y1={CENTER}
            y2={hourTip.y}
          />
          <line
            stroke={INK}
            strokeLinecap="round"
            strokeWidth={4}
            x1={CENTER}
            x2={minuteTip.x}
            y1={CENTER}
            y2={minuteTip.y}
          />
        </>
      ) : null}
      <circle cx={CENTER} cy={CENTER} fill={INK} r={5} />
    </svg>
  );
}
